/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { api, ApiError } from "core/api";
import { Button, Card, Form, Input, Select, Switch } from "core/components";
import { navigateTo } from "core/navigation";
import { RoleFormData, RoleSchema } from "core/schema";
import { Option } from "core/types";
import { admin_roles_path, admin_role_path } from "routes";

type RoleFormProps = {
  role: RoleFormData;
  departments: Array<Option>;
};

const RoleForm = ({ role, departments }: RoleFormProps): JSX.Element => {
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<RoleFormData>({
    defaultValues: RoleSchema.cast(role),
    resolver: yupResolver(RoleSchema)
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);

  const save = async (data: RoleFormData): Promise<unknown> => {
    try {
      if (data.id) {
        await api.put(admin_role_path(role), data);
      } else {
        await api.post(admin_roles_path(role), data);
      }

      navigateTo(admin_roles_path());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  return (
    <React.StrictMode>
      <Form errors={formErrors}>
        <Card>
          <Card.Header title={role.id ? "Edit Role" : "New Role"}>
            <Button
              icon="arrow-left"
              iconProps={{ className: "mr-3" }}
              className="button--default mr-3"
              disabled={isSubmitting}
              title="Back"
              onClick={() => navigateTo(admin_roles_path())}
            />
            <Button
              icon="save"
              iconProps={{ className: "mr-3" }}
              className="button--brand mr-3"
              disabled={isSubmitting}
              title="Save"
              onClick={handleSubmit(async (data) => save(data))}
            />
          </Card.Header>
          <Card.Body>
            <Container>
              <Row>
                <Col lg={3} />
                <Col>
                  <Controller
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        label="Active"
                        info="Active or Inactive"
                        checked={field.value}
                        value={field.value?.toString()}
                        errorMessage={errors.active?.message}
                      />
                    )}
                  />
                  <Controller
                    name="department"
                    control={control}
                    as={Select}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Department"
                        required
                        options={departments}
                        placeholder="Select a Department"
                        onChange={(option: Option) => field.onChange(option?.value)}
                        value={departments.find((department) => department.value == field.value)}
                        errorMessage={errors.department?.message}
                      />
                    )}
                  />
                  <Input {...register("name")} label="Name" required errorMessage={errors.name?.message} maxLength={100} />
                </Col>
                <Col lg={3} />
              </Row>
            </Container>
          </Card.Body>
        </Card>
      </Form>
    </React.StrictMode>
  );
};

export default RoleForm;
